<script lang="ts" setup>
import { evaluate } from 'mathjs';
import type { IonInput } from '#build/components';
import { captureException } from '@sentry/capacitor';

interface Props {
  allowNegative: boolean,
}
interface Emits {
  (event: 'update:value', value: number): void;
}
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const input = ref<InstanceType<typeof IonInput> | null>(null);

const expression = ref<string>('');
const { t } = useEkI18n();
defineExpose({
  setFocus,
  resetCalculator
});

function isExpressionValid(expression: string) {
  try {
    evaluate(expression);

    return true;
  } catch (error) {
    captureException(error);

    return false;
  }
}

function evaluateExpression() {
  const trimmedExpression = expression.value.trim();

  if (!trimmedExpression) {
    emit('update:value', 0);

    return;
  }

  const formattedExpression = trimmedExpression.replace(/,/g, '.');

  if (!isExpressionValid(formattedExpression)) {
    return;
  }

  try {
    const result = evaluate(formattedExpression);
    let roundedResult = +result.toFixed(2);

    if (roundedResult === 0) {
      roundedResult = 0;
    }

    if (typeof result === 'number') {
      if (!props.allowNegative && roundedResult < 0) {
        emit('update:value', 0);
      } else {
        emit('update:value', roundedResult);
      }
    }
  } catch (error) {
    captureException(error);
  }
}

function setFocus() {
  input.value?.$el.setFocus();
}

function resetCalculator() {
  if (expression.value !== '') {
    expression.value = '';
  }
}
</script>

<template>
  <IonItem>
    <IonLabel position="stacked">
      {{ t('materials.label.calculator') }}
    </IonLabel>
    <IonInput
      ref="input"
      v-model="expression"
      :clear-input="true"
      aria-label="Calculator"
      :placeholder="t('labels.enterExpression')"
      @input="evaluateExpression"
    />
  </IonItem>
</template>

<style scoped>
</style>
